import { useCallback, useEffect, useMemo, useRef } from "react";
import { useAuth } from "@clerk/react";
import { CreatorsTable } from "@/components/creators-table";
import type {
  CreatorsTableQuery,
  CreatorsTableServer,
} from "@/components/creators-table/types";
import { useCreatorsStore } from "@/stores";
import { useGlobalCrmStore } from "@/stores/creators/globalCrmStore";

// Typing in the search box coalesces into one request; everything else
// (page, sort, checklist) fires right away — the tiny delay only folds the
// table's "reset to page 1" re-render into the same fetch.
const SEARCH_DEBOUNCE_MS = 300;
const COALESCE_MS = 30;

/**
 * Global CRM — every campaign creator in the org in one creators table, with
 * Campaign + Inbox columns. Same table as a campaign's Creators tab, but the
 * rows are a server page: the table reports its query (search, filters,
 * sort, page) and this page fetches it (see CreatorsTableServer). Load
 * errors surface as toasts (globalCrmStore).
 */
export default function GlobalCrmPage() {
  const { orgId, getToken } = useAuth();
  const { creators, setCreators } = useCreatorsStore();
  const { total, campaigns, facets, loading, loaded, error, fetchPage, reset } =
    useGlobalCrmStore();

  const timerRef = useRef<number | undefined>(undefined);
  const lastQueryRef = useRef<CreatorsTableQuery | null>(null);

  // Fresh page per org: drop whatever roster the store held (a campaign's)
  // and forget the previous org's counts.
  useEffect(() => {
    setCreators([]);
    reset();
    lastQueryRef.current = null;
    return () => window.clearTimeout(timerRef.current);
  }, [orgId, setCreators, reset]);

  const run = useCallback(
    async (query: CreatorsTableQuery, full: boolean) => {
      if (!orgId) return;
      const token = await getToken();
      if (!token) return;
      await fetchPage(query, orgId, token, { full });
    },
    [orgId, getToken, fetchPage]
  );

  const onQueryChange = useCallback(
    (query: CreatorsTableQuery) => {
      const previous = lastQueryRef.current;
      if (previous && JSON.stringify(previous) === JSON.stringify(query)) return;
      const searchChanged = previous?.globalFilter !== query.globalFilter;
      lastQueryRef.current = query;
      window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(
        () => void run(query, !useGlobalCrmStore.getState().loaded),
        searchChanged && previous ? SEARCH_DEBOUNCE_MS : COALESCE_MS
      );
    },
    [run]
  );

  // After approve / reject / delete / transfer: same query, fresh counts.
  const refetch = useCallback(async () => {
    const query = lastQueryRef.current;
    if (query) await run(query, true);
  }, [run]);

  const initialLoading = !loaded && !error;
  const server: CreatorsTableServer = useMemo(
    () => ({
      rowCount: total,
      loading,
      initialLoading,
      facets,
      campaigns,
      onQueryChange,
      refetch,
    }),
    [total, loading, initialLoading, facets, campaigns, onQueryChange, refetch]
  );

  return (
    <div className="flex flex-col w-full h-full">
      <CreatorsTable data={creators} setData={setCreators} server={server} />
    </div>
  );
}
